import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "@/features/shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  summarySection: {
    marginTop: 16,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    minWidth: 200,
  },
});

export const SalesPaymentPdf = ({ salesPayment, user }) => {
  const totalAllocatedAmount = salesPayment.salesInvoicePayments.reduce(
    (sum, payment) => sum + payment.allocatedAmount,
    0
  );

  const totalDiscount = salesPayment.salesInvoicePayments.reduce(
    (sum, payment) => sum + payment.discount,
    0
  );

  const grandTotal = totalAllocatedAmount - totalDiscount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Penerimaan Penjualan</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {salesPayment.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Penjual</Text>
            <Text style={styles.attributeHeader}>
              {salesPayment.seller.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Pembeli</Text>
            <Text style={styles.attributeHeader}>
              {salesPayment.buyer.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(dayjs(salesPayment.transactionDate))}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Cash Bank</Text>
            <Text style={styles.attributeHeader}>
              {salesPayment.outletChartOfAccount?.name ?? "-"}
            </Text>
          </View>
          <View style={[styles.infoItem, { flex: 2 }]}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {salesPayment.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Invoice Payments Table */}
        <Text style={styles.sectionTitle}>Detail Pembayaran Faktur</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 3 }]}>No. Faktur #</Text>
          <Text style={[styles.cell, { flex: 2 }]}>Tanggal</Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Total Faktur
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Terutang
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Bayar
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Diskon
          </Text>
        </View>
        {salesPayment.salesInvoicePayments.map((payment) => (
          <View style={styles.tableRow} key={payment.id}>
            <Text style={[styles.cell, { flex: 3 }]}>
              {payment.salesInvoice.referenceNumber || "-"}
            </Text>
            <Text style={[styles.cell, { flex: 2 }]}>
              {formatDate(dayjs(payment.salesInvoice.transactionDate))}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.salesInvoice.grandTotal)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.outstandingAmount)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.allocatedAmount)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.discount)}
            </Text>
          </View>
        ))}

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text>Total Diterima:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(totalAllocatedAmount)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Total Diskon:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(totalDiscount)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>Grand Total:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(grandTotal)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

SalesPaymentPdf.propTypes = {
  salesPayment: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
